/**
 * Shared site footer — redesign pass C (item 16).
 *
 * Rendered on every view (landing, configurator, checkout, success):
 * wordmark + gradient-dot motif, key links, contact details and the
 * privacy / terms modals. It consolidates the configurator's old
 * footer-ish privacy link into one surface.
 *
 * Key links are plain anchors to the landing sections: on the landing
 * itself they smooth-scroll in place; on any other view the browser
 * navigates to `/#<section>` and lands on the anchor natively.
 */

import { useState, type MouseEvent } from 'react';
import { useLandingContent } from '../lib/content';
import { scrollToSection } from '../lib/scrollToSection';
import { PrivacyStatementModal } from './modals/PrivacyStatementModal';
import { TermsModal } from './modals/TermsModal';

// Company contact details (DDG Europe). The order-receiving inbox is
// promotions@ddg-europe.com; the footer surfaces it as the public contact.
const CONTACT_EMAIL = 'promotions@ddg-europe.com';
const CONTACT_PHONE_DISPLAY = '+31 (0)35 541 1482';
const CONTACT_PHONE_TEL = '+31355411482';

// DDG Europe offices — HQ first, then display order.
const OFFICES = [
  {
    country: 'The Netherlands — HQ',
    lines: ['Baarnsche Dijk 4-B; Office BG-01', '3741 LR Baarn, Netherlands'],
  },
  {
    country: 'Belgium',
    lines: ['Spaces Tour & Taxis Brussels', 'Rue Picard 7 Box 100', '1000 Brussels, Belgium'],
  },
  {
    country: 'Portugal',
    lines: ['Praça Dom Francisco Gomes 2, 2nd Floor', '8000-168 Faro, Portugal'],
  },
  {
    country: 'United Kingdom',
    lines: ['International House, 64 Nile Street', 'N1 7SR, London, UK'],
  },
  {
    country: 'Spain',
    lines: ['Gran Via de les Corts Catalanes, 583', "L'Eixample, 08011 Barcelona, Spain"],
  },
] as const;

const KEY_LINKS = [
  { id: 'how-it-works', label: 'How it works' },
  { id: 'choose-your-box', label: 'Build your box' },
  { id: 'contact', label: 'Contact' },
] as const;

interface Props {
  /** Extra classes on the <footer> (e.g. bottom clearance for sticky bars). */
  className?: string;
}

export function SiteFooter({ className }: Props) {
  const hero = useLandingContent('hero');
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  const email = CONTACT_EMAIL;

  const handleKeyLink = (e: MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    // Same-view anchor → smooth scroll.
    if (scrollToSection(id)) return;
    // The app is a single-path SPA, so from the builder/checkout/success a
    // plain `/#section` link is a hash-only change: the browser doesn't
    // reload and the landing (which owns the anchor) never mounts. Set the
    // hash, then force a real navigation; the landing scrolls to the hash
    // on mount.
    window.location.hash = id;
    window.location.reload();
  };

  return (
    <footer
      data-testid="site-footer"
      className={`relative z-10 bg-[#356d4e] text-white font-century ${className ?? ''}`}
    >
      <div className="max-w-[90%] mx-auto pt-14 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 pb-12">
          {/* Wordmark + tagline */}
          <div className="space-y-4">
            <a
              href="/"
              className="inline-flex items-baseline gap-1.5 font-bold text-lg tracking-[0.1em]"
            >
              {hero.title}
              <span aria-hidden="true" className="w-2 h-2 gradient-dot" />
            </a>
            <p className="text-sm text-white/80 leading-relaxed max-w-[36ch]">
              {hero.subtitle}
            </p>
          </div>

          {/* Key links */}
          <nav aria-label="Footer" className="space-y-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">
              Explore
            </p>
            <ul className="space-y-2.5">
              {KEY_LINKS.map((link) => (
                <li key={link.id}>
                  <a
                    href={`/#${link.id}`}
                    onClick={(e) => handleKeyLink(e, link.id)}
                    className="text-sm text-white/80 hover:text-white hover:underline underline-offset-4 decoration-[#57c858] transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact */}
          <div className="space-y-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">
              Get in touch
            </p>
            <ul className="space-y-2.5 text-sm text-white/80">
              <li>
                <a
                  href={`mailto:${email}`}
                  className="hover:text-white hover:underline underline-offset-4 decoration-[#57c858] transition-colors"
                >
                  {email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${CONTACT_PHONE_TEL}`}
                  className="hover:text-white hover:underline underline-offset-4 decoration-[#57c858] transition-colors"
                >
                  {CONTACT_PHONE_DISPLAY}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Our offices */}
        <div className="border-t border-white/15 pt-10 pb-12">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60 mb-6">
            Our offices
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-8">
            {OFFICES.map((office) => (
              <address key={office.country} className="not-italic">
                <span className="block text-sm font-bold text-white mb-1.5">
                  {office.country}
                </span>
                <span className="block text-xs text-white/70 leading-relaxed">
                  {office.lines.map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </span>
              </address>
            ))}
          </div>
        </div>

        {/* Legal bar */}
        <div className="border-t border-white/15 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-white/60">
            © {new Date().getFullYear()} DDG Europe. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <button
              type="button"
              onClick={() => setPrivacyOpen(true)}
              className="text-xs text-white/70 hover:text-white underline underline-offset-4 decoration-white/30 hover:decoration-[#57c858] transition-colors"
            >
              Privacy statement
            </button>
            <button
              type="button"
              onClick={() => setTermsOpen(true)}
              className="text-xs text-white/70 hover:text-white underline underline-offset-4 decoration-white/30 hover:decoration-[#57c858] transition-colors"
            >
              Terms &amp; conditions
            </button>
          </div>
        </div>
      </div>

      <PrivacyStatementModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    </footer>
  );
}
